import {
  QueryKeys,
  useBulkGetCanonicalCategoriesByEvents,
  useBulkGetCanonicalTagsByEvents,
  useBulkGetCommunities,
  useBulkGetParentCommunityIds,
  useBulkGetProfiles,
  useBulkGetSessionLocations,
  useGetBulkEvents,
  useGetBulkSessionData,
  useGetBulkSessionFavoriteCountsV2,
  useGetUserFavoriteSessions,
} from '@sunrise-ui/api-client';
import type { SessionOutput } from '@sunrise-ui/api/events/api';
import { getSpeakerIds } from '@utils/session';
import { useMemo } from 'react';

const {
  sessionsKey,
  tagsKey,
  categoriesKey,
  attendeesKey,
  favoritesKey,
  favoriteCountKey,
  locationsKey,
  userSessionFavoritesKey,
  canonical,
} = QueryKeys.sessions;
const { eventsKey } = QueryKeys.events;
const { profileKey } = QueryKeys.profiles;
const { communitiesKey, byIdKey, parentsKey } = QueryKeys.communities;

interface UseSessionListDataFetchingOptions {
  sessions?: SessionOutput[];
  communityId?: string;
  userId?: string;
  isLoading?: boolean;
}

export function useSessionListDataFetching({
  sessions,
  communityId,
  userId,
  isLoading = true,
}: UseSessionListDataFetchingOptions) {
  // Extract session and event IDs
  const sessionIds = useMemo(
    () => sessions?.map((session) => session.id) || [],
    [sessions],
  );

  const eventIds = useMemo(
    () => Array.from(new Set(sessions?.map((session) => session.event_id))),
    [sessions],
  );

  // Bulk fetch canonical tags and categories
  useBulkGetCanonicalTagsByEvents(eventIds);
  useBulkGetCanonicalCategoriesByEvents(eventIds);

  // Bulk fetch events
  const { data: events, isLoading: isLoadingEvents } =
    useGetBulkEvents(eventIds);

  // Extract event community IDs
  const eventCommunityIds = useMemo(
    () => Array.from(new Set(events?.map((event) => event.community_id))),
    [events],
  );

  // Bulk fetch session data
  const { data: bulkSessionData } = useGetBulkSessionData({
    sessionIds,
  });

  // Bulk fetch session favorite counts
  useGetBulkSessionFavoriteCountsV2({
    sessionIds,
  });

  useGetUserFavoriteSessions({
    userId,
  });

  // Bulk fetch session locations
  useBulkGetSessionLocations(eventIds);

  // Extract speaker IDs and bulk fetch speaker profiles
  const speakerIds = useMemo(
    () => getSpeakerIds(sessions, bulkSessionData),
    [sessions, bulkSessionData],
  );
  useBulkGetProfiles(speakerIds);

  // Combine event community IDs with any additional community IDs for parent fetching
  const communityIdsForParents = useMemo(
    () =>
      communityId ? [...eventCommunityIds, communityId] : eventCommunityIds,
    [eventCommunityIds, communityId],
  );

  // Bulk fetch parent community IDs
  const {
    data: bulkParentCommunityIdsData,
    isLoading: isLoadingParentCommunityIds,
  } = useBulkGetParentCommunityIds({
    communityIds: communityIdsForParents,
    enabled: !isLoading,
  });

  // Extract all community IDs (event communities + their parents)
  const allCommunityIds = useMemo(() => {
    const ids = [...eventCommunityIds];
    if (bulkParentCommunityIdsData) {
      Object.values(bulkParentCommunityIdsData).forEach((parentIds) => {
        ids.push(...parentIds);
      });
    }
    return Array.from(new Set(ids));
  }, [bulkParentCommunityIdsData, eventCommunityIds]);

  // Bulk fetch community profiles
  useBulkGetCommunities({
    communityIds: allCommunityIds,
    enabled: !isLoadingEvents && !isLoadingParentCommunityIds,
  });

  // Return query keys that this hook is responsible for
  const queryKeys = new Set([
    [eventsKey],
    [sessionsKey],
    [sessionsKey, favoritesKey, favoriteCountKey],
    [sessionsKey, userSessionFavoritesKey],
    [sessionsKey, locationsKey],
    [sessionsKey, tagsKey],
    [sessionsKey, tagsKey, canonical],
    [sessionsKey, categoriesKey],
    [sessionsKey, categoriesKey, canonical],
    [sessionsKey, attendeesKey],
    [profileKey],
    [communitiesKey, byIdKey],
    [communitiesKey, parentsKey, true],
  ]);

  return {
    // Query keys this hook manages
    queryKeys,
  };
}
